import React from 'react';
import { Routes, Route, Link } from 'react-router-dom';
import About from './pages/About';
import Services from './pages/Services';
import Packages from './pages/Packages';
import Gallery from './pages/Gallery';
import Testimonials from './pages/Testimonials';
import Contact from './pages/Contact';
import GiftCard from './pages/GiftCard';
import Booking from './pages/Booking';
import JoinPage from './pages/JoinPage';
import JoinSuccessPage from './pages/JoinSuccessPage';
import CircularGallery from './components/CircularGallery';
import Navbar from './components/Navbar';
import Home from './pages/Home';
import Login from './pages/Login';
import Signup from './pages/Signup';
import ResetPassword from './pages/ResetPassword';
import ProfilePage from './pages/customer/ProfilePage';
import Dashboard from './pages/admin/Dashboard';
import UserManagement from './pages/admin/UserManagement';
import ContentManagement from './pages/admin/ContentManagement';
import SchedulingSystem from './pages/admin/SchedulingSystem';
import TeamManagement from './pages/admin/TeamManagement';
import AdminLayout from './components/admin/AdminLayout';
import CrewLayout from './components/crew/CrewLayout';
import CrewDashboard from './pages/crew/CrewDashboard';
import CrewSchedule from './pages/crew/CrewSchedule';
import CrewProfile from './pages/crew/CrewProfile';
import { AuthProvider } from './contexts/auth/AuthContext';
import { ServiceProvider } from './contexts/services/ServiceContext';
import { NotificationProvider } from './contexts/notifications/NotificationContext';
import ProtectedRoute from './contexts/auth/ProtectedRoute';
import AdminRoute from './contexts/auth/AdminRoute';
import CrewRoute from './contexts/auth/CrewRoute';
import ScrollToTop from './components/ScrollToTop';

// Main App component with routes and layout
function App() {
  return (
    <AuthProvider>
      <ServiceProvider>
      <NotificationProvider>
      <ScrollToTop />
      <div className="min-h-screen bg-gradient-to-br from-[#F8F6F2] via-[#E8F5F2] to-[#F9E9E9] font-sans flex flex-col">
        {/* Navigation Bar */}
        <Navbar />
        {/* Main content with top padding for fixed navbar */}
        <div className="pt-20 flex-1 flex flex-col">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/services" element={<Services />} />
            <Route path="/packages" element={<Packages />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/testimonials" element={<Testimonials />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/gift-card" element={<GiftCard />} />
            <Route path="/booking" element={<Booking />} />

            {/* Membership Routes */}
            <Route path="/join" element={
              <ProtectedRoute>
                <JoinPage />
              </ProtectedRoute>
            } />
            <Route path="/join-success" element={
              <ProtectedRoute>
                <JoinSuccessPage />
              </ProtectedRoute>
            } />

            {/* Auth routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            {/* Customer Profile */}
            <Route path="/profile" element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            } />

            {/* Admin Routes */}
            <Route path="/admin" element={
              <AdminRoute>
                <AdminLayout>
                  <Dashboard />
                </AdminLayout>
              </AdminRoute>
            } />
            <Route path="/admin/dashboard" element={
              <AdminRoute>
                <AdminLayout>
                  <Dashboard />
                </AdminLayout>
              </AdminRoute>
            } />
            <Route path="/admin/users" element={
              <AdminRoute>
                <AdminLayout>
                  <UserManagement />
                </AdminLayout>
              </AdminRoute>
            } />
            <Route path="/admin/content" element={
              <AdminRoute>
                <AdminLayout>
                  <ContentManagement />
                </AdminLayout>
              </AdminRoute>
            } />
            <Route path="/admin/team" element={
              <AdminRoute>
                <AdminLayout>
                  <TeamManagement />
                </AdminLayout>
              </AdminRoute>
            } />
            <Route path="/admin/schedule" element={
              <AdminRoute>
                <AdminLayout>
                  <SchedulingSystem />
                </AdminLayout>
              </AdminRoute>
            } />

            {/* Crew Routes */}
            <Route path="/crew" element={
              <CrewRoute>
                <CrewLayout>
                  <CrewDashboard />
                </CrewLayout>
              </CrewRoute>
            } />
            <Route path="/crew/dashboard" element={
              <CrewRoute>
                <CrewLayout>
                  <CrewDashboard />
                </CrewLayout>
              </CrewRoute>
            } />
            <Route path="/crew/schedule" element={
              <CrewRoute>
                <CrewLayout>
                  <CrewSchedule />
                </CrewLayout>
              </CrewRoute>
            } />
            <Route path="/crew/profile" element={
              <CrewRoute>
                <CrewLayout>
                  <CrewProfile />
                </CrewLayout>
              </CrewRoute>
            } />
          </Routes>
          {/* Footer - Only show on main site, not admin or crew */}
          {!window.location.pathname.includes('/admin') && !window.location.pathname.includes('/crew') && (
            <footer className="w-full bg-gradient-to-b from-[#e8f5f2] to-[#b2d8c5]/30 text-[#232347] pt-16 pb-8 px-4 mt-auto">
              <div className="max-w-7xl mx-auto flex flex-col md:flex-row gap-12 md:gap-0 justify-between items-start md:items-stretch">
                {/* Left: Social & Brand */}
                <div className="flex-1 flex flex-col gap-6 mb-10 md:mb-0">
                  <div className="flex gap-4 text-2xl">
                    <a href="#" aria-label="Instagram" className="hover:text-[#B2D8C5] transition"><i className="fab fa-instagram" />📸</a>
                    <a href="#" aria-label="Facebook" className="hover:text-[#B2D8C5] transition"><i className="fab fa-facebook" />📘</a>
                    <a href="#" aria-label="X" className="hover:text-[#B2D8C5] transition"><i className="fab fa-x-twitter" />✖️</a>
                  </div>
                  <div className="mt-4 text-lg font-serif italic text-[#232347]/80">Relax, recharge, and renew—your wellness journey starts here.</div>
                </div>
                {/* Center: Newsletter Signup */}
                <div className="flex-1 flex flex-col items-center md:items-start gap-4">
                  <div className="uppercase text-xs font-bold tracking-widest text-[#232347]/70 mb-2">Newsletter</div>
                  <div className="text-2xl md:text-3xl font-serif font-semibold mb-2 text-[#232347]">Join Our Newsletter</div>
                  <div className="text-[#232347]/70 mb-2 text-center md:text-left">Get the latest updates and exclusive offers. Select your preferred location for tailored content.</div>
                  <form className="w-full max-w-xs flex flex-col gap-3">
                    <input type="email" placeholder="Email address" className="rounded bg-[#e8f5f2] border border-[#B2D8C5] px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#B2D8C5]" />
                    <select className="rounded bg-[#e8f5f2] border border-[#B2D8C5] px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#B2D8C5]">
                      <option>Choose Location</option>
                      <option>Dallas</option>
                    </select>
                    <button type="submit" className="mt-2 bg-[#B2D8C5] text-white font-semibold py-2 rounded shadow hover:bg-[#88bfa3] transition">Sign Me Up</button>
                  </form>
                </div>
                {/* Right: Link Groups */}
                <div className="flex-1 grid grid-cols-2 gap-8 md:gap-12">
                  <div>
                    <div className="uppercase text-xs font-bold tracking-widest mb-3 text-[#232347]/70">Services</div>
                    <ul className="space-y-2">
                      <li><a href="#" className="hover:text-[#B2D8C5] transition">Day Spa</a></li>
                      <li><a href="#" className="hover:text-[#B2D8C5] transition">Med Spa</a></li>
                      <li><a href="#" className="hover:text-[#B2D8C5] transition">Spa 101</a></li>
                    </ul>
                  </div>
                  <div>
                    <div className="uppercase text-xs font-bold tracking-widest mb-3 text-[#232347]/70">Community</div>
                    <ul className="space-y-2">
                      <li><a href="#" className="hover:text-[#B2D8C5] transition">Membership</a></li>
                      <li><a href="#" className="hover:text-[#B2D8C5] transition">Blog</a></li>
                      <li><a href="#" className="hover:text-[#B2D8C5] transition">Join Our Team</a></li>
                    </ul>
                  </div>
                  <div>
                    <div className="uppercase text-xs font-bold tracking-widest mb-3 text-[#232347]/70">Shop</div>
                    <ul className="space-y-2">
                      <li><a href="#" className="hover:text-[#B2D8C5] transition">Shop All</a></li>
                      <li><a href="#" className="hover:text-[#B2D8C5] transition">Gift Cards & Specials</a></li>
                      <li><a href="#" className="hover:text-[#B2D8C5] transition">Products</a></li>
                    </ul>
                  </div>
                  <div>
                    <div className="uppercase text-xs font-bold tracking-widest mb-3 text-[#232347]/70">Location</div>
                    <ul className="space-y-2">
                      <li><a href="#" className="hover:text-[#B2D8C5] transition">4222 Trinity Mills Rd Suite 120, Dallas, TX 75024</a></li>
                    </ul>
                  </div>
                </div>
              </div>
              <div className="text-center text-[#B2D8C5] mt-12 text-sm font-serif">© 2025 Healing Lotus. All rights reserved.</div>
            </footer>
          )}
        </div>
      </div>
      </NotificationProvider>
      </ServiceProvider>
    </AuthProvider>
  );
}

export default App;
